import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import { BoldIcon, ItalicIcon, LinkIcon, UnderlineIcon } from '../components/icons';
import { askText } from '../components/AskText';
import { textToHtml } from './Compose';

/**
 * The signature, formatted: bold, italic, underline and links — what a
 * signature needs, no more. Saved as HTML; an older plain-text signature
 * opens as paragraphs.
 */
export function SignatureEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (html: string) => void;
}): JSX.Element {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: { openOnClick: false, autolink: true },
        heading: false,
        codeBlock: false,
      }),
    ],
    content: value.trim().startsWith('<') ? value : textToHtml(value),
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? '' : e.getHTML()),
  });
  const tool = (
    label: string,
    Icon: (p: { className?: string }) => JSX.Element,
    active: boolean,
    run: () => void,
  ) => (
    <button
      type="button"
      className={`icon-btn${active ? ' on-tool' : ''}`}
      aria-label={label}
      title={label}
      aria-pressed={active}
      onMouseDown={(e) => e.preventDefault()}
      onClick={run}
    >
      <Icon />
    </button>
  );
  return (
    <div className="signature-editor">
      {editor && (
        <div className="editor-tools" role="toolbar" aria-label="Signature formatting">
          {tool('Bold', BoldIcon, editor.isActive('bold'), () =>
            editor.chain().focus().toggleBold().run(),
          )}
          {tool('Italic', ItalicIcon, editor.isActive('italic'), () =>
            editor.chain().focus().toggleItalic().run(),
          )}
          {tool('Underline', UnderlineIcon, editor.isActive('underline'), () =>
            editor.chain().focus().toggleUnderline().run(),
          )}
          {tool('Link', LinkIcon, editor.isActive('link'), async () => {
            const href = await askText({
              title: 'Link',
              label: 'Link address',
              initial: (editor.getAttributes('link').href as string | undefined) ?? 'https://',
              action: 'Save',
            });
            if (href && /^(https?:|mailto:)/i.test(href.trim())) {
              editor.chain().focus().extendMarkRange('link').setLink({ href: href.trim() }).run();
            } else if (href === '') {
              editor.chain().focus().extendMarkRange('link').unsetLink().run();
            }
          })}
        </div>
      )}
      <EditorContent editor={editor} className="editor signature" aria-label="Signature" />
    </div>
  );
}
